import { Stack, type Theme, Typography, useMediaQuery } from "@mui/material";
import { Empty, Flex } from "@zfp-akademie-a-s/zfp-design";
import { EvaluationIcon } from "Components/_Shared/Icons/Icons";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useContext } from "react";
import { InvestmentsNsureEvaluation, InvestmentsNsureEvaluationAlert, hasEvaluationContent } from "./InvestmentsNsureEvaluation";
import { InvestmentsNsureSelectionCard } from "./InvestmentsNsureSelectionCard";
import { NsureSelectionContext, NsureSelectionItemType } from "./InvestmentsNsureSelectionContext";

type Props = {
  readonly hideEvaluationWhenStacked?: boolean;
};

// The right column, 412px with its 32px padding and the divider line on its left, which stretches
// down beside the whole left column. The drawer renders in a portal outside ScopedCssBaseline, so
// the 100% column needs border-box explicitly, otherwise its padding widens the tablet layout into
// a sideways scroll.
const panelColumnSx = {
  width: { xs: "100%", lg: 412 },
  boxSizing: "border-box",
  flexShrink: 0,
  pl: { xs: 0, lg: Spacing.XXXL },
  borderLeft: { xs: "none", lg: "1px solid" },
  borderColor: { lg: "divider" },
} as const;

export const InvestmentsNsureEvaluationPanel = ({ hideEvaluationWhenStacked }: Props) => {
  const { t } = useResource();
  const { selection, evaluation, showInstrumentSearch } = useContext(NsureSelectionContext);
  const isStacked = useMediaQuery((theme: Theme) => theme.breakpoints.down("lg"));
  const showEvaluation = !(hideEvaluationWhenStacked && isStacked);

  const isInstrument = selection ? selection.type === NsureSelectionItemType.Instrument : showInstrumentSearch;

  return (
    <Stack spacing={Spacing.L} sx={panelColumnSx}>
      {/* the column title, as the left column's */}
      <Flex alignItems="center" minHeight={40}>
        <Typography variant="h3">
          {isInstrument
            ? t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.SelectionPanel.SelectedInstrument)
            : t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.SelectionPanel.SelectedPortfolio)}
        </Typography>
      </Flex>

      {selection ? (
        // stays in view while the list beside it scrolls
        <Stack spacing={Spacing.XL + Spacing.S} sx={{ position: { lg: "sticky" }, top: 0 }}>
          <InvestmentsNsureSelectionCard selection={selection}>
            {showEvaluation && hasEvaluationContent(selection, evaluation) && <InvestmentsNsureEvaluation />}
          </InvestmentsNsureSelectionCard>
          {showEvaluation && <InvestmentsNsureEvaluationAlert />}
        </Stack>
      ) : (
        <Empty
          iconSize={40}
          icon={<EvaluationIcon sx={{ color: "text.disabled" }} />}
          text={t(Resources.AnalysisDetail.GoalsFulfillment.Nsure.Evaluation.SelectPortfolio)}
        />
      )}
    </Stack>
  );
};
